import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CameraIcon } from '@heroicons/react/24/outline';

import { useStateContext } from '../../../contexts/ContextProvider';
import { useHousekeepingQuickReport } from '../../../hooks/useHousekeepingQuickReport';
import { usePannes } from '../../api/usePannes';
import { useTraductionsMetier } from '../../api/useTraductionsMetier';
import Button from '../../components/Button';
import EtatErreur from '../../components/EtatErreur';
import IndicateurActualisation from '../../components/IndicateurActualisation';
import Pills from '../../components/Pills';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import { messageVide, ongletsMaintenance, trierPannes } from '../../helpers/maintenance';
import type { OngletMaintenance } from '../../helpers/maintenance';
import { echecSansDonnee } from '../../helpers/requete';
import { t } from '../../i18n/langue';
import { useTirerPourActualiser } from '../../hooks/useTirerPourActualiser';
import EnteteApp from '../../shell/EnteteApp';
import { useCoque } from '../../shell/CoqueContext';
import FeuilleSignalement from '../menage/FeuilleSignalement';

import CartePanne from './CartePanne';
import FeuilleChoixLieu from './FeuilleChoixLieu';

/**
 * La liste « Maintenance » du technicien, en trois onglets : à prendre (ouvertes
 * et libres), mes interventions (ouvertes, à moi), terminées (récentes). Les
 * urgences remontent en tête. Sondage de 60 s, comme « Mes chambres ».
 *
 * « Signaler » : le technicien déclare lui-même une panne, avec la même feuille
 * photo + vocal que les agents (`useHousekeepingQuickReport`), après avoir choisi
 * la chambre ou l'espace.
 */
export default function ListePannes() {
    const { ouvrirPanneau } = useCoque();
    const { currentFacility } = useStateContext();
    const navigate = useNavigate();
    const message = (useLocation().state as { message?: string } | null)?.message;

    const [onglet, setOnglet] = useState<OngletMaintenance>('a_prendre');
    const { data, isLoading, isError, isFetching, refetch } = usePannes(currentFacility?.id ?? null, onglet);
    const signalement = useHousekeepingQuickReport();
    const echec = echecSansDonnee({ isError, data });
    const pannes = trierPannes(data ?? []);
    const traductions = useTraductionsMetier(pannes.map((panne) => panne.category_name));

    const refCorps = useRef<HTMLDivElement>(null);
    const [actualisationDemandee, setActualisationDemandee] = useState(false);
    const actualiser = () => {
        setActualisationDemandee(true);
        void refetch();
    };
    const { distance, pret } = useTirerPourActualiser(refCorps, actualiser);
    useEffect(() => {
        if (!isFetching) setActualisationDemandee(false);
    }, [isFetching]);

    return (
        <Screen
            refCorps={refCorps}
            sansRebond
            entete={<EnteteApp titre={t('maintenance.titre')} onOuvrirPanneau={ouvrirPanneau} />}
        >
            <IndicateurActualisation distance={distance} pret={pret} enCours={actualisationDemandee && isFetching} />

            <div className="flex flex-col gap-3 border-b border-border-color-light bg-white px-4 py-3">
                <Pills libelle={t('maintenance.choisirListe')} options={ongletsMaintenance()} valeur={onglet} onChange={setOnglet} />
                <div className="flex items-center justify-between gap-3">
                    <Button
                        libelle={t('maintenance.signalerPanne')}
                        icone={CameraIcon}
                        variante="secondaire"
                        onPress={() => signalement.start('issue')}
                    />
                    {!isLoading && (
                        <Button libelle={t('maintenance.actualiser')} variante="lien" espacement="aucun" onPress={actualiser} />
                    )}
                </div>
                {/* L'input vit ici : l'appareil photo ne s'ouvre que dans le geste. */}
                <input {...signalement.inputProps} data-testid="photo-signalement" />
            </div>

            <div className="flex flex-col gap-3 px-4 py-4">
                {message && (
                    <div role="status" className="rounded-xl bg-main-color-light px-3 py-2">
                        <Text variante="petit" ton="marque">
                            {message}
                        </Text>
                    </div>
                )}
                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}
                {echec && <EtatErreur onReessayer={() => void refetch()} />}
                {!isLoading && !echec && pannes.length === 0 && (
                    <Text variante="petit" ton="attenue">
                        {messageVide(onglet)}
                    </Text>
                )}
                {pannes.map((panne) => (
                    <CartePanne
                        key={panne.id}
                        panne={panne}
                        avecStatut={onglet !== 'a_prendre'}
                        categorie={traductions.libelle(panne.category_name)}
                        onOuvrir={() => navigate(`/maintenance/${panne.id}`)}
                    />
                ))}
            </div>

            {signalement.step === 'room' && (
                <FeuilleChoixLieu onChoisir={signalement.pickRoom} onFermer={signalement.cancel} />
            )}
            {signalement.isReviewOpen && <FeuilleSignalement signalement={signalement} />}
        </Screen>
    );
}
